import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * 🎨 UI State Service
 * Maneja el estado global de la interfaz de usuario siguiendo principios SOLID
 * - Single Responsibility: Solo maneja estado UI
 * - Open/Closed: Extensible para nuevos estados UI sin modificar código existente
 * - DRY: Reutilizable para cualquier estado booleano UI
 * - KISS: API simple y clara
 */
@Injectable({
  providedIn: 'root'
})
export class UiStateService {
  
  // 💾 STORAGE KEY - DRY: centralizado (DEBE estar antes que BehaviorSubject)
  private readonly STORAGE_KEYS = {
    MENU_COLLAPSED: 'domoblock_menu_desktop_collapsed'
  } as const;
  
  // 🔒 PRIVATE - Principio de encapsulación
  private readonly _isMenuDesktopCollapsed = new BehaviorSubject<boolean>(this.getInitialMenuState());
  
  // 📡 PUBLIC OBSERVABLES - Solo lectura desde componentes
  public readonly isMenuDesktopCollapsed$: Observable<boolean> = this._isMenuDesktopCollapsed.asObservable();

  constructor() {
    // 🔄 Auto-persistencia: Guardar cambios automáticamente
    this.isMenuDesktopCollapsed$.subscribe(collapsed => {
      this.saveToStorage(this.STORAGE_KEYS.MENU_COLLAPSED, collapsed);
    });
  }

  // 📖 GETTERS - KISS: API simple
  public get isMenuDesktopCollapsed(): boolean {
    return this._isMenuDesktopCollapsed.value;
  }

  // 🎛️ ACTIONS - Open/Closed: Fácil de extender
  public toggleMenuDesktop(): void {
    this._isMenuDesktopCollapsed.next(!this._isMenuDesktopCollapsed.value);
  }

  public collapseMenuDesktop(): void {
    this._isMenuDesktopCollapsed.next(true);
  }

  public expandMenuDesktop(): void {
    this._isMenuDesktopCollapsed.next(false);
  }

  public setMenuDesktopState(collapsed: boolean): void {
    this._isMenuDesktopCollapsed.next(collapsed);
  }

  // Navigation return state for investment-opportunity → invest-real-estate flow
  private returnToInvestRealEstate: string | null = null;

  public setReturnToInvestRealEstate(projectId: string): void {
    this.returnToInvestRealEstate = projectId;
  }

  public getReturnToInvestRealEstate(): string | null {
    return this.returnToInvestRealEstate;
  }

  public clearReturnToInvestRealEstate(): void {
    this.returnToInvestRealEstate = null;
  }

  // 🔧 PRIVATE HELPERS - DRY: Funciones reutilizables
  private getInitialMenuState(): boolean {
    return false;
  }

  private getFromStorage<T>(key: string, defaultValue: T): T {
    try {
      const stored = localStorage.getItem(key);
      return stored !== null ? JSON.parse(stored) : defaultValue;
    } catch {
      return defaultValue;
    }
  }

  private saveToStorage<T>(key: string, value: T): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Silently fail if localStorage is not available
    }
  }

  // 🔮 FUTURE EXTENSIBILITY - Open/Closed Principle
  // Aquí se pueden agregar fácilmente más estados UI sin tocar código existente:
  
  // private readonly _isDarkMode = new BehaviorSubject<boolean>(false);
  // public readonly isDarkMode$: Observable<boolean> = this._isDarkMode.asObservable();
  // public toggleDarkMode(): void { ... }
  
  // private readonly _isCompactMode = new BehaviorSubject<boolean>(false);
  // public readonly isCompactMode$: Observable<boolean> = this._isCompactMode.asObservable();
  // public toggleCompactMode(): void { ... }
} 